import React, { useState, useEffect, useCallback, useRef } from 'react';
import './LabelSelectionModal.scss';
import { getLabelsByUser, updateLabel } from '../../services/api/LabelsApi';
import { getCourtTypeByCourtRef } from '../../services/api/MyClientsApi';
import { useToast } from '../../context/ToastContext';

const LabelSelectionModal = ({
  isOpen,
  onClose,
  courtRef,
  onLabelAdded,
  setModalTab,
  setShowModal,
  onEditLabel
}) => {
  const [selectionType, setSelectionType] = useState('new');
  const [availableLabels, setAvailableLabels] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { showToast } = useToast();
  const modalRef = useRef(null);


  const fetchLabels = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getLabelsByUser();

      // Extract labels from the nested structure
      const responseData = response?.data || {};
      const labelGroups = responseData.labels || [];

      // Flatten the nested labels structure
      const allLabels = [];
      labelGroups.forEach(group => {
        if (group.labels && Array.isArray(group.labels)) {
          allLabels.push(...group.labels);
        }
      });

      setAvailableLabels(allLabels);
    } catch (error) {
      console.error('Error fetching labels:', error);
      setAvailableLabels([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {

      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    // Add event listener when the modal is open
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup function to remove the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose])

  // Fetch available labels when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset form state when modal opens
      setSelectedLabels([]);
      setSearchTerm('');
      setIsDropdownOpen(false);
      setSelectionType('new');

      fetchLabels();
    }
  }, [isOpen, fetchLabels]);

  // Filter labels to show all available labels (allow assigning same label to different courts)
  const filteredLabels = Array.isArray(availableLabels)
    ? availableLabels.filter(label => {
      // Ensure label has required properties
      if (!label || !label.name) return false;

      // Filter by search term if provided
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        return label.name.toLowerCase().includes(searchLower) ||
          (label.description && label.description.toLowerCase().includes(searchLower));
      }

      return true; // Show all labels - allow assigning same label to different courts
    })
    : [];

  const handleLabelSelect = (labelId) => {
    if (labelId && !selectedLabels.includes(labelId)) {
      setSelectedLabels(prev => [...prev, labelId]);
    }
    setIsDropdownOpen(false);
    setSearchTerm('');
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setIsDropdownOpen(true);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(prev => !prev);
  };

  const handleDropdownBlur = (e) => {
    // Close dropdown if clicking outside
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setTimeout(() => setIsDropdownOpen(false), 150);
    }
  };

  const handleRemoveSelectedLabel = (labelIdToRemove) => {
    setSelectedLabels(prev => prev.filter(id => id !== labelIdToRemove));
  };

  const handleAddNewLabel = () => {
    // Close this modal and open the label form modal
    handleCloseModal();
    setModalTab("Labels");
    setShowModal(true);
    onEditLabel(null);
  };

  const handleCloseModal = () => {
    // Reset form state when closing modal
    setSelectedLabels([]);
    setSearchTerm('');
    setIsDropdownOpen(false);
    setSelectionType('new');
    onClose();
  };

  const handleAddSelectedLabels = async () => {
    if (!Array.isArray(selectedLabels) || selectedLabels.length === 0) return;

    console.log(' Starting label assignment process...');
    console.log(' Selected Labels:', selectedLabels);
    console.log(' Court Ref:', courtRef);

    setLoading(true);
    let skippedLabels = [];
    let processedLabels = 0;

    try {
      // Add each selected label to the court
      for (const labelId of selectedLabels) {
        if (!labelId) continue; // Skip invalid label IDs

        console.log(` Processing Label ID: ${labelId}`);

        // First, get the current label data to preserve existing court assignments
        console.log('Fetching current label data to preserve existing assignments...');
        const currentLabel = availableLabels.find(label => label._id === labelId);

        if (!currentLabel) {
          console.error('Could not find label in availableLabels:', labelId);
          continue;
        }

        console.log(' Current label data:');
        console.log('   - High Court Cases:', currentLabel.highCourtCases || []);
        console.log('   - District Court Cases:', currentLabel.districtCourtCases || []);
        console.log('   - Other Court Cases:', currentLabel.otherCourtCases || []);

        // Check if this court reference already exists for this label using API
        try {
          console.log(` Checking if court ${courtRef} is already assigned to label ${currentLabel.name}...`);
          const courtTypeResponse = await getCourtTypeByCourtRef(courtRef);

          // If the API returns data, check if this specific label is already assigned to this court
          if (courtTypeResponse && courtTypeResponse.data) {
            const courtType = await getCourtTypeFromRef(courtRef);
            const courtCasesField = `${courtType}CourtCases`;
            const existingCourtCases = currentLabel[courtCasesField] || [];

            if (existingCourtCases.includes(courtRef)) {
              console.log(` Label "${currentLabel.name}" is already assigned to court ${courtRef}, skipping...`);
              skippedLabels.push(currentLabel.name);
              continue;
            }
          }
        } catch (error) {
          // If API call fails, continue with the assignment (court might not exist yet)
          console.log('Court reference not found in API, proceeding with assignment...');
        }

        const courtType = await getCourtTypeFromRef(courtRef);
        console.log(' Court Type determined:', courtType);

        // Get existing court cases for this type
        const courtCasesField = `${courtType}CourtCases`;
        const existingCourtCases = currentLabel[courtCasesField] || [];

        console.log(` Existing ${courtCasesField}:`, existingCourtCases);

        // Double-check if court is already assigned (additional safety check)
        if (existingCourtCases.includes(courtRef)) {
          console.log('Court already assigned to this label (double-check), skipping...');
          skippedLabels.push(currentLabel.name);
          continue;
        }

        // Merge existing court cases with new one
        const updatedCourtCases = [...existingCourtCases, courtRef];
        console.log(` Updated ${courtCasesField}:`, updatedCourtCases);

        const labelData = {
          // Preserve all existing court cases and add new one
          highCourtCases: courtType === 'high' ? updatedCourtCases : (currentLabel.highCourtCases || []),
          districtCourtCases: courtType === 'district' ? updatedCourtCases : (currentLabel.districtCourtCases || []),
          otherCourtCases: courtType === 'other' ? updatedCourtCases : (currentLabel.otherCourtCases || [])
        };

        console.log(' Sending API request with MERGED data:', JSON.stringify(labelData, null, 2));
        console.log(' API Call: updateLabel(labelId, labelData)');
        console.log('   - Label ID:', labelId);
        console.log('   - Data:', labelData);

        const apiResponse = await updateLabel(labelId, labelData);

        console.log(' API Response received:');
        console.log('   - Status:', apiResponse?.status);
        console.log('   - Status Text:', apiResponse?.statusText);
        console.log('   - Response Data:', JSON.stringify(apiResponse?.data, null, 2));
        console.log('   - Full Response Object:', apiResponse);

        if (apiResponse?.data) {
          console.log(' Updated Label Data:');
          console.log('   - Label Name:', apiResponse.data.name);
          console.log('   - High Court Cases:', apiResponse.data.highCourtCases);
          console.log('   - District Court Cases:', apiResponse.data.districtCourtCases);
          console.log('   - Other Court Cases:', apiResponse.data.otherCourtCases);
        }

        processedLabels++;
      }

      console.log(' All labels processed successfully!');

      // Show appropriate toast messages
      if (skippedLabels.length > 0) {
        const skippedMessage = skippedLabels.length === 1
          ? `Label "${skippedLabels[0]}" is already assigned to this court.`
          : `${skippedLabels.length} labels (${skippedLabels.join(', ')}) are already assigned to this court.`;
        showToast(skippedMessage, 'warning');
      }

      if (processedLabels > 0) {
        const successMessage = processedLabels === 1
          ? `1 label has been successfully added to this court.`
          : `${processedLabels} labels have been successfully added to this court.`;
        showToast(successMessage, 'success');
      }

      // If no labels were processed at all, show info message
      if (processedLabels === 0 && skippedLabels.length === 0) {
        showToast('No labels were processed.', 'info');
      }

      // Notify parent component that labels were added (only if some were processed)
      if (processedLabels > 0 && onLabelAdded && typeof onLabelAdded === 'function') {
        console.log(' Calling onLabelAdded callback...');
        await onLabelAdded(selectedLabels.filter((labelId) => {
          const label = availableLabels.find(l => l._id === labelId);
          return label && !skippedLabels.includes(label.name);
        }));
        console.log(' onLabelAdded callback completed');
      }

      // Reset form state
      setSelectedLabels([]);
      setSearchTerm('');
      setIsDropdownOpen(false);

      // Close the modal
      onClose();

      console.log(' Label assignment process completed successfully!');
    } catch (error) {
      console.error(' Error adding labels:');
      console.error('   - Error Message:', error.message);
      console.error('   - Error Stack:', error.stack);
      console.error('   - Full Error Object:', error);

      if (error.response) {
        console.error('🚨 API Error Response:');
        console.error('   - Status:', error.response.status);
        console.error('   - Status Text:', error.response.statusText);
        console.error('   - Response Data:', JSON.stringify(error.response.data, null, 2));
        console.error('   - Response Headers:', error.response.headers);
      }

      if (error.request) {
        console.error('📡 Request Details:', error.request);
      }

      // Show error toast notification
      showToast('Error occurred while adding labels. Please try again.', 'error');
    } finally {
      setLoading(false);
      console.log('handleAddSelectedLabels function completed');
    }
  };

  // Helper function to determine court type from courtRef using API
  const getCourtTypeFromRef = async (courtRef) => {
    if (!courtRef) {
      console.log(' No court ref provided, defaulting to OTHER');
      return 'other';
    }

    console.log(' Calling API to determine court type for:', courtRef);

    try {
      const response = await getCourtTypeByCourtRef(courtRef);

      console.log(' API Response for court type:');
      console.log('   - Status:', response?.status);
      console.log('   - Response Data:', JSON.stringify(response?.data, null, 2));

      // Extract court type from API response
      const courtType = response?.data?.source || response?.data?.courtType || response?.data?.type || 'other';

      console.log('Court type determined from API:', courtType);

      // Normalize the court type to match expected values
      const normalizedType = courtType.toLowerCase();
      if (normalizedType.includes('high')) {
        console.log(' Normalized as: high');
        return 'high';
      } else if (normalizedType.includes('district') || normalizedType.includes('dist')) {
        console.log(' Normalized as: district');
        return 'district';
      } else {
        console.log(' Normalized as: other');
        return 'other';
      }

    } catch (error) {
      console.error(' Error calling court type API:');
      console.error('   - Error Message:', error.message);
      console.error('   - Full Error:', error);

      if (error.response) {
        console.error('API Error Response:');
        console.error('   - Status:', error.response.status);
        console.error('   - Data:', error.response.data);
      }


    }
  };

  // Fallback function for court type detection if API fails


  if (!isOpen) return null;

  return (
    <div className="label-selection-modal-backdrop">
      <div ref={modalRef} className="label-selection-modal">
        <h2 className="label-selection-modal-title">Add Label</h2>

        <div className="label-selection-options">
          {/* Add New Label Option */}
          <label className="label-selection-option">
            <input
              type="radio"
              value="new"
              checked={selectionType === 'new'}
              onChange={(e) => setSelectionType(e.target.value)}
            />
            <span>Add New Label</span>
          </label>
          <p className="label-selection-desc">
            Create a completely new label with custom properties.
          </p>

          {/* Existing Label Option */}
          <label className="label-selection-option">
            <input
              type="radio"
              value="existing"
              checked={selectionType === 'existing'}
              onChange={(e) => setSelectionType(e.target.value)}
            />
            <span>Assign Existing Label</span>
          </label>
          <p className="label-selection-desc">
            Select from your existing labels to assign to this court.
          </p>
        </div>

        {/* Show existing labels dropdown when "existing" is selected */}
        {selectionType === 'existing' && (
          <div className="existing-labels-section">
            <div className="searchable-dropdown-container" onBlur={handleDropdownBlur}>
              <label htmlFor="label-search" className="dropdown-label">
                Search and select labels to add:
              </label>

              <div className="searchable-dropdown">
                <div className="search-input-container">
                  <input
                    id="label-search"
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onFocus={() => setIsDropdownOpen(true)}
                    placeholder={loading ? "Loading labels..." : "Type to search labels..."}
                    className="search-input"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={toggleDropdown}
                    className="dropdown-toggle-btn"
                    disabled={loading}
                  >
                    ▼
                  </button>
                </div>

                {isDropdownOpen && (
                  <div className="dropdown-options">
                    {loading ? (
                      <div className="dropdown-option loading-option">Loading labels...</div>
                    ) : filteredLabels.length === 0 ? (
                      <div className="dropdown-option no-results-option">
                        {searchTerm ? `No labels found for "${searchTerm}"` : "No labels available"}
                      </div>
                    ) : (
                      filteredLabels.map(label => (
                        <div
                          key={label._id}
                          onClick={() => handleLabelSelect(label._id)}
                          className={`dropdown-option ${selectedLabels.includes(label._id) ? 'selected' : ''}`}
                        >
                          <div className="option-content">
                            <span className="option-name">{label.name}</span>
                            {label.description && (
                              <span className="option-description">{label.description}</span>
                            )}
                          </div>
                          {selectedLabels.includes(label._id) && (
                            <span className="selected-indicator">✓</span>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Display selected labels */}
            {Array.isArray(selectedLabels) && selectedLabels.length > 0 && (
              <div className="selected-labels-container">
                <h4 className="selected-labels-title">Selected Labels:</h4>
                <div className="selected-labels-list">
                  {selectedLabels.map(labelId => {
                    const label = availableLabels.find(l => l._id === labelId);
                    return label ? (
                      <div key={labelId} className="selected-label-item">
                        <div className="label-info">
                          <span className="label-name">{label.name}</span>
                          {label.description && (
                            <span className="label-description">{label.description}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveSelectedLabel(labelId)}
                          className="remove-label-btn"
                          title="Remove label"
                        >
                          ×
                        </button>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {Array.isArray(selectedLabels) && selectedLabels.length > 0 && (
              <div className="selected-count">
                {selectedLabels.length} label{selectedLabels.length !== 1 ? 's' : ''} selected
              </div>
            )}
          </div>
        )}

        <div className="label-selection-actions">
          <button className="btn-cancel" onClick={handleCloseModal}>
            Cancel
          </button>

          {selectionType === 'new' ? (
            <button className="btn-primary" onClick={handleAddNewLabel}>
              Create New Label
            </button>
          ) : (
            <button
              className="btn-primary"
              onClick={handleAddSelectedLabels}
              disabled={!Array.isArray(selectedLabels) || selectedLabels.length === 0 || loading}
            >
              {loading ? 'Adding...' : `Add Selected Labels (${Array.isArray(selectedLabels) ? selectedLabels.length : 0})`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LabelSelectionModal;
